import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Button, CircularProgress, Alert, Paper, FormControl, InputLabel, Select, MenuItem, Stack } from '@mui/material';
import { userEndpoints } from '../config/api';
import { useAuth } from '../context/AuthContext';

export default function ApiKeysPage() {
  const { user, getAccessToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState(null);
  const [apiKeyId, setApiKeyId] = useState(null);
  const [usagePlanId, setUsagePlanId] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const userId = user?.attributes?.sub;

  const fetchApiKey = useCallback(async () => {
    if (!userId) return;
    setError(null);
    try {
      const token = await getAccessToken();
      const response = await fetch(`${userEndpoints.apiKey}?user-id=${userId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to fetch API key');
      }
      const data = await response.json();
      setApiKey(data.apiKey);
      setApiKeyId(data.apiKeyId);
      setUsagePlanId(data.usagePlanId);
    } catch (err) {
      setApiKey(null);
      setApiKeyId(null);
      setUsagePlanId(null);
      setError(err.message || 'Failed to fetch API key');
    }
  }, [getAccessToken, userId]);

  const fetchAvailablePlans = useCallback(async () => {
    if (!userId) return;
    try {
      const token = await getAccessToken();
      const response = await fetch(userEndpoints.availablePlans, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to load usage plans');
      const data = await response.json();
      const plans = data.usagePlans || [];
      setAvailablePlans(plans);
      setSelectedPlan((prev) => prev || (plans[0]?.id || ''));
    } catch (err) {
      setAvailablePlans([]);
      setError(err.message || 'Failed to load usage plans');
    }
  }, [getAccessToken, userId]);

  const handleCreateApiKey = async () => {
    if (!userId || !selectedPlan) return;
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const token = await getAccessToken();
      const response = await fetch(`${userEndpoints.apiKey}?user-id=${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ usagePlanId: selectedPlan })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Failed to create API key');
      }
      setApiKey(data.apiKey);
      setApiKeyId(data.apiKeyId);
      setUsagePlanId(data.usagePlanId);
      setSuccess('API key created successfully');
    } catch (err) {
      setError(err.message || 'Failed to create API key');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteApiKey = async () => {
    if (!userId) return;
    setDeleting(true);
    setError(null);
    setSuccess(null);
    try {
      const token = await getAccessToken();
      const response = await fetch(`${userEndpoints.apiKey}?user-id=${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete API key');
      }
      setApiKey(null);
      setApiKeyId(null);
      setUsagePlanId(null);
      setSuccess(data.message || 'API key removed');
    } catch (err) {
      setError(err.message || 'Failed to delete API key');
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchApiKey(), fetchAvailablePlans()]);
      setLoading(false);
    };
    if (userId) {
      load();
    }
  }, [fetchApiKey, fetchAvailablePlans, userId]);

  return (
    <Box maxWidth={600} mx="auto" mt={4}>
      <Typography variant="h4" gutterBottom>API Key Management</Typography>
      <Stack spacing={2}>
        {loading && <CircularProgress />}
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}

        {!loading && (
          <Paper sx={{ p: 3, mt: 2 }}>
            {apiKey ? (
              <>
                <Typography variant="subtitle1">Your API Key:</Typography>
                <Typography variant="body1" sx={{ wordBreak: 'break-all', fontWeight: 'bold', mb: 2 }}>{apiKey}</Typography>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>Usage Plan: {usagePlanId}</Typography>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDeleteApiKey}
                  disabled={deleting}
                >
                  {deleting ? <CircularProgress size={20} /> : 'Delete API Key'}
                </Button>
              </>
            ) : (
              <>
                <Typography sx={{ mb: 2 }}>No API key found for your account. Choose a usage plan and request one.</Typography>
                {availablePlans.length > 0 ? (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="usage-plan-select">Usage Plan</InputLabel>
                    <Select
                      labelId="usage-plan-select"
                      label="Usage Plan"
                      value={selectedPlan}
                      onChange={(e) => setSelectedPlan(e.target.value)}
                    >
                      {availablePlans.map((plan) => (
                        <MenuItem key={plan.id} value={plan.id}>
                          {plan.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Alert severity="info" sx={{ mb: 2 }}>No usage plans available. Please contact an administrator.</Alert>
                )}
                <Button
                  variant="contained"
                  onClick={handleCreateApiKey}
                  disabled={!selectedPlan || submitting}
                >
                  {submitting ? <CircularProgress size={20} /> : 'Request API Key'}
                </Button>
              </>
            )}
          </Paper>
        )}
      </Stack>
    </Box>
  );
}
